// Base notification schemas — template-managed. Do not edit; add customisations in ../../custom/index.ts instead.
import { NOTIFICATION_CHANNEL_TYPES_API_ZOD_ENUM } from "@gosaas/types/notification";
import { z } from "zod";

export const CreateChannelSchema = z
	.object({
		name: z.string().min(1, "Name is required"),
		type: z.enum(NOTIFICATION_CHANNEL_TYPES_API_ZOD_ENUM),
		email: z.string().optional(),
		url: z.string().optional(),
		secret: z.string().optional(),
	})
	.superRefine((data, ctx) => {
		if (data.type === "email") {
			if (!data.email?.trim()) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Email is required",
					path: ["email"],
				});
			} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Invalid email",
					path: ["email"],
				});
			}
		}
		if (data.type === "webhook") {
			if (!data.url?.trim()) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Webhook URL is required",
					path: ["url"],
				});
			} else {
				try {
					new URL(data.url);
				} catch {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: "Invalid URL",
						path: ["url"],
					});
				}
			}
		}
	});

export type CreateChannelInput = z.infer<typeof CreateChannelSchema>;

export const UpdateChannelSchema = z.object({
	name: z.string().min(1, "Name is required").optional(),
	email: z.string().email().optional(),
	url: z.string().url().optional(),
	secret: z.string().optional(),
});

export type UpdateChannelInput = z.infer<typeof UpdateChannelSchema>;

export const NotificationRuleChannelSchema = z
	.object({
		channel_id: z.string().optional(),
		predefined: z.string().optional(),
		target_user_id: z.string().optional(),
	})
	.refine(
		(val) =>
			(val.channel_id || val.predefined) && !(val.channel_id && val.predefined),
		{ message: "Specify either a channel or a predefined target, not both" },
	);

export const CreateRuleSchema = z.object({
	name: z.string().min(1, "Name is required"),
	description: z.string(),
	event_types: z.array(z.string()).min(1, "Select at least one event"),
	conditions: z
		.object({
			usage_threshold: z
				.object({
					feature_key: z.string().optional(),
					threshold_percents: z.array(z.number()).min(1),
				})
				.optional(),
		})
		.optional(),
	is_enabled: z.boolean(),
	channels: z
		.array(NotificationRuleChannelSchema)
		.min(1, "At least one channel is required"),
});

export type CreateRuleInput = z.infer<typeof CreateRuleSchema>;

export const UpdateRuleSchema = z.object({
	name: z.string().min(1, "Name is required").optional(),
	description: z.string().optional(),
	event_types: z.array(z.string()).min(1).optional(),
	conditions: z
		.object({
			usage_threshold: z
				.object({
					feature_key: z.string().optional(),
					threshold_percents: z.array(z.number()).min(1),
				})
				.optional(),
		})
		.optional(),
	is_enabled: z.boolean().optional(),
	channels: z.array(NotificationRuleChannelSchema).optional(),
});

export type UpdateRuleInput = z.infer<typeof UpdateRuleSchema>;
